import type { Approval } from '~/hooks/queries/use-approvals';

/**
 * FRONT-1323: what the header's pending-approvals badge should do.
 *
 * The badge used to have exactly one job — open the Inspector. Once the
 * Inspector can be switched off (`eddy.show-inspector`) that stops being a
 * useful action, but hiding the badge with it is worse: `AppHeader` mounts on
 * every route, so this is the only cross-page signal that something is waiting
 * on the user, and everything else that renders approvals
 * (`pending-approval-card.tsx`, the phone surface's equivalent) filters to the
 * ONE chat currently open. So the badge stays and its destination moves.
 *
 * Three outcomes, in order of preference:
 *  - `inspector` — the panel is still on, so keep the old behaviour.
 *  - `chat` — the panel is off; open the chat that raised an approval, where the
 *    in-chat card (FRONT-1311) can answer it.
 *  - `none` — the panel is off AND no pending approval carries a chat origin
 *    (`originExternalId` is nullable; the Inspector's unfiltered list was the
 *    only surface that ever showed one of those). The badge still reports the
 *    count — that is real information — but disables itself rather than
 *    pretending a click leads somewhere.
 *
 * Pure and exported so the decision is unit-pinned independently of the header's
 * markup, which has no render test — same reason `inspector-indicator.ts` and
 * `jump-pill-logic.ts` sit beside their components.
 */
export type PendingApprovalsTarget = 'inspector' | 'chat' | 'none';

export interface PendingApprovalsAffordance {
  /** How many pending approvals the badge reports. */
  count: number;
  /** Whether the badge renders at all. */
  visible: boolean;
  target: PendingApprovalsTarget;
  /**
   * Something is pending AND nothing can answer it. Not the same as
   * `target === 'none'`: with zero approvals "none carries a chat origin" is
   * trivially true, so `target` is `none` on every empty list (FRONT-2757).
   */
  deadEnd: boolean;
  /** The chat to open when `target` is `chat`, else null. */
  chatOrigin: string | null;
  /** Accessible name AND tooltip — the badge's visible count is just a number. */
  hint: string;
}

export function resolvePendingApprovalsAffordance(
  approvals: Approval[],
  inspectorEnabled: boolean,
): PendingApprovalsAffordance {
  const count = approvals.length;
  // First one carrying a chat origin. Deliberately not "the newest" — any
  // origin chat is a better destination than a disabled button, and once the
  // user is in a thread its own card lists every approval raised from it.
  const chatOrigin = approvals.find((a) => a.originExternalId)?.originExternalId ?? null;

  let target: PendingApprovalsTarget;
  if (inspectorEnabled) target = 'inspector';
  else if (chatOrigin) target = 'chat';
  else target = 'none';

  const visible = count > 0;
  const label = `${count} pending approval${count === 1 ? '' : 's'}`;
  let hint: string;
  if (target === 'inspector') hint = `${label} — click to review in the Inspector`;
  else if (target === 'chat') hint = `${label} — click to open the chat that asked`;
  else hint = `${label} — waiting on a request with no chat to open`;

  return {
    count,
    visible,
    target,
    deadEnd: visible && target === 'none',
    chatOrigin: target === 'chat' ? chatOrigin : null,
    hint,
  };
}
